import { useEffect, useMemo, useRef } from "react";
import { useObraEmbarcacoes } from "@/modules/obras";
import { periodoDaObraNoWizard, readEquipe } from "../../../../hooks/nova-obra.equipe";
import { useNovaObra } from "../../../../hooks/nova-obra.context";
import { todayIso } from "@/modules/carga-trabalho/carga-trabalho.utils";
import { useEquipeObraOpcoes } from "@/modules/recursos/use-equipe-obra-opcoes";
import { NOVA_OBRA_TITLE } from "../../../../nova-obra.constants";
import { novasDaEquipe, obraDaNovaObra } from "../../../../hooks/equipe-verificacoes";
import { useVerificacoesEquipe } from "../../../../hooks/use-verificacoes-equipe";
import type { DivergenciasEquipeDialogsProps } from "./types-divergencias-equipe-dialogs";
import { divergenciasParaVerificacao } from "@/modules/recursos";

export function useDivergenciasEquipeDialogs({
  divergencias,
  onConfirmar,
  onVoltar,
}: DivergenciasEquipeDialogsProps) {
  const { state } = useNovaObra();
  const today = useMemo(() => todayIso(), []);
  const periodo = periodoDaObraNoWizard(state.etapas.duracao) ?? { inicio: today, fim: today };
  const opcoes = useEquipeObraOpcoes(periodo.inicio, periodo.fim);
  const embarcacoes = useObraEmbarcacoes();
  const embarcacao = embarcacoes.data.find((item) => item.id === state.obra.embarcacaoId);

  const papeisById = useMemo(
    () => new Map(opcoes.papeis.map((papel) => [papel.id, papel])),
    [opcoes.papeis],
  );
  const novas = novasDaEquipe(readEquipe(state.etapas.equipe).linhas, papeisById);
  const obra = obraDaNovaObra(embarcacao?.nome ?? NOVA_OBRA_TITLE, periodo);

  const verificacoes = useVerificacoesEquipe({
    obra,
    // Só reabre os diálogos do servidor: não há Salvar a validar aqui.
    validar: () => false,
    novas,
    resumoObrigatorios: [],
    listagem: opcoes.profissionais,
    onErroVerificacao: () => undefined,
    acaoFinal: (justificativa) => {
      if (justificativa) onConfirmar(justificativa);
    },
  });

  // Reabre uma vez, quando as listas dos nomes chegarem (com erro, segue sem os nomes).
  const reaberto = useRef(false);
  useEffect(() => {
    if (reaberto.current || opcoes.status === "loading") return;
    reaberto.current = true;
    const { conflitos, indisponiveis } = divergenciasParaVerificacao(
      divergencias,
      novas,
      obra,
      (usuarioId) => opcoes.profissionais.find((item) => item.id === usuarioId)?.nome ?? "",
      (papelObraId) => papeisById.get(papelObraId)?.nome,
    );
    verificacoes.reabrirComDivergencias(conflitos, indisponiveis);
  });

  const voltar = () => {
    verificacoes.cancelar();
    onVoltar();
  };

  return { obra, verificacoes, voltar };
}
